import { useMemo } from 'react';

import type { BrannanWebsiteHeaderSettings, WebsiteHeaderPhotoSchema_PhotoBackground, WebsitePhotoCropInfo } from '@shared/types/WebsiteHeaderSettings.types';

import { PHOTO_PRESENTATION_CONFIG } from '@shared/utils/photoPresentationLayout';

import { isRecord } from '@shared/utils/isRecord';

import { motionNamesColor, motionNavColor } from './multiPhotoMotion.options';
import { useColorPaletteProvider } from '../../../../../ColorPaletteProvider/ColorPaletteProvider';
import { useHeaderMotionConfigState } from './useHeaderMotionConfig';

type BrannanWebsiteHeader = BrannanWebsiteHeaderSettings['websiteHeader'];

const HERO_ASPECT = PHOTO_PRESENTATION_CONFIG.photoBackground.aspectRatio as number;
const HERO_MOBILE_ASPECT = PHOTO_PRESENTATION_CONFIG.photoBackground.mobileAspectRatio as number;

/** Height (0–1 from the top) a crop of a too-tall photo centres on: faces sit above the middle. */
const FACE_LINE = 0.4;

/**
 * The crop of a `width`×`height` photo that exactly fills a frame of `aspect` (width / height).
 * Centred across, and on `FACE_LINE` down. The full-bleed renderer draws the crop at the frame's aspect, so a crop of any other shape is
 * stretched; a Slider or Marquee crop was made for a wide strip, so it cannot be reused. Pure.
 */
export const coverCrop = (width: number, height: number, aspect: number): WebsitePhotoCropInfo => {
  if (!(width > 0 && height > 0 && aspect > 0)) {
    return { x: 0, y: 0, width: 100, height: 100, unit: 'percent' };
  }
  const photoAspect = width / height;
  if (photoAspect > aspect) {
    const cropWidth = (aspect / photoAspect) * 100;
    return { x: (100 - cropWidth) / 2, y: 0, width: cropWidth, height: 100, unit: 'percent' };
  }
  const cropHeight = (photoAspect / aspect) * 100;
  // Taller than the frame: centre the crop at FACE_LINE rather than the middle, where faces usually
  // are not, so a portrait photo in a wide hero keeps heads in frame instead of showing torsos.
  const y = Math.min(Math.max(FACE_LINE * 100 - cropHeight / 2, 0), 100 - cropHeight);
  return { x: 0, y, width: 100, height: cropHeight, unit: 'percent' };
};

const ENTITIES: Readonly<Record<string, string>> = { '&amp;': '&', '&lt;': '<', '&gt;': '>', '&quot;': '"', '&#39;': "'", '&nbsp;': ' ' };

/**
 * Plain text of a Slider/Marquee rich title (`eventName.contentHtml`): tags dropped (a line break or
 * block end becomes a space), the common entities decoded, whitespace collapsed. Pure and SSR-safe,
 * because it never touches the DOM; the full-bleed header renders it as text, so nothing here is HTML.
 */
export const titleTextFromHtml = (html: string): string =>
  html
    .replace(/<br\s*\/?>|<\/(p|div|h[1-6])>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&(amp|lt|gt|quot|#39|nbsp);/g, entity => ENTITIES[entity] ?? entity)
    .replace(/\s+/g, ' ')
    .trim();

/** Motion types that present a Slider/Marquee header's photos their own way, on a full-bleed header. */
export type MultiPhotoMotionType = 'showcase' | 'polaroid';

const MULTI_PHOTO_TYPES: ReadonlySet<string> = new Set<MultiPhotoMotionType>(['showcase', 'polaroid']);

const isMultiPhotoMotionType = (type: unknown): type is MultiPhotoMotionType => typeof type === 'string' && MULTI_PHOTO_TYPES.has(type);

/** Whether a header's saved `motion` asks for a multi-photo type (before the flag and registry have their say). */
const isMultiPhotoMotion = (motion: unknown): boolean => isRecord(motion) && motion.enabled === true && isMultiPhotoMotionType(motion.type);

/** The names colour over the photos when neither the couple nor the template picked one. */
const DEFAULT_MULTI_PHOTO_NAMES_COLOR = '#FFFFFF';

/**
 * Shows a Slider or Marquee header as the full-bleed header a multi-photo Motion type plays on. Pure.
 *
 * Returns `websiteHeader` itself unless it is a `photoSlider` / `photoMarquee` header with at least one
 * photo and `type` is set (the type resolved on). Otherwise returns a new `photoBackground` header with
 * the names centred (the header's own title when it has one, as the Slider shows), the same `motion`, and
 * runtime-only `motionPhotos`. The names colour is the couple's own (Text Color), else the template's
 * `namesColor`, else white over the photos:
 * - `showcase`: the first photo as the hero, cropped to its shape (`coverCrop`), names centred over it,
 *   and the remaining photos as the collage's tiles. Names and nav share the colour, both over the photo.
 * - `polaroid`: no hero photo (the collage sits on the page colour), names centred on top of it, and
 *   every photo as a Polaroid. The colour reaches the names only; the nav sits on the page and keeps its
 *   own colour: the template's `navColor`, else the page's text colour (`pageFontColor`), as the
 *   plain Slider's nav has, so it reads on dark pages too.
 * Nothing is saved: the stored header stays a Slider/Marquee, so turning the type (or the flag) off
 * brings the strip straight back.
 */
export const toMultiPhotoMotionHeader = (
  websiteHeader: BrannanWebsiteHeader,
  type: MultiPhotoMotionType | null,
  isPending = false,
  pageFontColor?: string,
): BrannanWebsiteHeader => {
  if (!type || !websiteHeader) {
    return websiteHeader;
  }
  const { headerPhoto } = websiteHeader;
  if (headerPhoto.presentationLayout !== 'photoSlider' && headerPhoto.presentationLayout !== 'photoMarquee') {
    return websiteHeader;
  }
  const [first, ...rest] = headerPhoto.photos ?? [];
  if (!first) {
    return websiteHeader;
  }
  // A Slider/Marquee shows its own rich title when the couple wrote one, else the event name; keep that.
  const customTitle = headerPhoto.eventName?.contentHtml ? titleTextFromHtml(headerPhoto.eventName.contentHtml) : '';
  // The couple's own names colour (Text Color) wins, then the template's `namesColor`, then white, which
  // reads over the photos. Turning a multi-photo Motion on drops any saved names colour
  // (`clearNamesColorForMultiPhotoMotion`), so a saved one is always the couple's own pick.
  const options = isRecord(headerPhoto.motion) ? headerPhoto.motion.options : undefined;
  const namesColor = headerPhoto.eventName?.color || motionNamesColor(options) || DEFAULT_MULTI_PHOTO_NAMES_COLOR;
  // Polaroid's nav sits on the page: the template's nav colour, else the page's own text colour. Without
  // either it would fall to the header default (#333333), unreadable on a dark page.
  const navColor = motionNavColor(options) ?? (type === 'polaroid' ? pageFontColor : undefined);
  const isPolaroid = type === 'polaroid';
  return {
    eventDisplayName: websiteHeader.eventDisplayName,
    headerPhoto: {
      // Never fetched or saved: the converted header only lives in memory for rendering.
      $id: headerPhoto.$id,
      presentationLayout: 'photoBackground',
      photos: isPolaroid
        ? []
        : [{ ...first, cropInfo: coverCrop(first.width, first.height, HERO_ASPECT), mobileCropInfo: coverCrop(first.width, first.height, HERO_MOBILE_ASPECT) }],
      eventName: {
        content: customTitle,
        hasModifiedEventNameOnce: customTitle !== '',
        textAlignment: 'center',
        pageAlignment: { x: 'center', y: 'center' },
      },
      // The names colour colours the names and the nav: here both sit over the photo together, so one
      // colour keeps them reading as a pair (the Slider's nav sits on the page instead).
      // Showcase: names and nav sit over the photo together, so they share the colour. Polaroid: the names
      // sit on the photos but the nav on the page, so the colour goes to the names alone.
      ...(isPolaroid
        ? { headerTitleStyle: { backgroundEffect: 'clear' as const, headerTextColor: namesColor } }
        : { colors: { nameAndNavFontColor: namesColor, nameAndNavBackgroundColor: namesColor, nameAndNavBackgroundColorEnabled: false } }),
      // A template nav colour (Polaroid: nav on the page) goes to the nav alone.
      ...(navColor ? { navigationStyle: { backgroundEffect: 'clear' as const, navigationTextColor: navColor } } : {}),
      motion: headerPhoto.motion,
      motionPhotos: isPolaroid ? [first, ...rest] : rest,
      ...(isPending ? { isMotionPending: true } : {}),
      ...(headerPhoto.eventName ? { motionSourceTitle: headerPhoto.eventName } : {}),
    },
  };
};

/**
 * Whether a header is a Slider/Marquee converted for a multi-photo Motion (it carries the runtime-only
 * collage photos) rather than a saved photo header. The legacy video-background override must skip
 * these: it replaces any `photoBackground` header and never mounts Motion.
 */
export const isConvertedMotionHeader = (headerPhoto: Pick<WebsiteHeaderPhotoSchema_PhotoBackground, 'motionPhotos'>): boolean => headerPhoto.motionPhotos !== undefined;

/**
 * Hook form of `toMultiPhotoMotionHeader` for the Brannan layouts: converts only while a multi-photo
 * type resolves on for the header (flag on, known type, multi-photo media), via `useHeaderMotionConfig`,
 * which reads the flag only for headers that ask for Motion. Memoised on its inputs.
 */
export const useMultiPhotoMotionHeader = (websiteHeader: BrannanWebsiteHeader): BrannanWebsiteHeader => {
  const headerPhoto = websiteHeader?.headerPhoto;
  const motion =
    headerPhoto && (headerPhoto.presentationLayout === 'photoSlider' || headerPhoto.presentationLayout === 'photoMarquee') && isMultiPhotoMotion(headerPhoto.motion)
      ? headerPhoto.motion
      : undefined;
  const { resolved, isLoading } = useHeaderMotionConfigState(motion, 'photos');
  const resolvedType = resolved && isMultiPhotoMotionType(resolved.def.id) ? resolved.def.id : null;
  // While the flag loads, convert to the saved type anyway but mark it pending: the header holds an
  // empty hero in its place rather than show the strip and then swap it (or play the opening unseen).
  const pendingType = isLoading && isRecord(motion) && isMultiPhotoMotionType(motion.type) ? motion.type : null;
  const type = resolvedType ?? pendingType;
  // Non-strict, as the repo reads this context elsewhere: the guest site mounts the provider above the
  // layouts (GuestSiteBody), but a render without it should fall back to the header default, not throw.
  const { pageFontColor } = useColorPaletteProvider(false) || {};
  return useMemo(() => toMultiPhotoMotionHeader(websiteHeader, type, isLoading, pageFontColor), [websiteHeader, type, isLoading, pageFontColor]);
};
